import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useApp, useT } from "@/context/AppContext";
import { usePartner } from "@/context/PartnerContext";
import { ShoppingCart, ChevronDown, User, LogOut, Settings, Menu, X, Globe, Coins } from "lucide-react";
import { CURRENCIES, LANGS } from "@/lib/i18n";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout, cart, lang, setLang, currency, setCurrency } = useApp();
  const { partner, prefix } = usePartner();
  const t = useT();
  const nav = useNavigate();
  const navigate = nav;
  const location = useLocation();
  const [mobile, setMobile] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Reset error state when partner changes
  React.useEffect(() => { setLogoFailed(false); }, [partner?.slug]);
  const cartCount = cart.reduce((n, x) => n + (x.quantity || 1), 0);

  const SUBSIDY_LABELS = { nl: "Subsidies", en: "Grants", de: "Förderungen", fr: "Subventions", es: "Subvenciones", it: "Sovvenzioni", da: "Tilskud" };
  const QUICKSTART_LABELS = { nl: "Quickstart", en: "Quickstart", de: "Schnellstart", fr: "Démarrage", es: "Inicio rápido", it: "Avvio rapido", da: "Hurtigstart" };
  const ABOUT_LABELS = { nl: "Over ons", en: "About us", de: "Über uns", fr: "À propos", es: "Sobre nosotros", it: "Chi siamo", da: "Om os" };
  const langPrefix = lang && lang !== "nl" ? `/${lang}` : "";
  const fullPrefix = `${prefix}${langPrefix}`;

  const navItems = [
    { to: `${fullPrefix}/quickstart`, label: QUICKSTART_LABELS[lang] || "Quickstart" },
    { to: `${fullPrefix}/configurator`, label: t.nav.configurator },
    { to: `${fullPrefix}/catalog`, label: t.nav.catalog },
    { to: `${fullPrefix}/bundles`, label: t.nav.bundles },
    { to: `${fullPrefix}/subsidies`, label: SUBSIDY_LABELS[lang] || "Subsidies" },
    { to: `${fullPrefix}/blog`, label: "Blog" },
    { to: `${fullPrefix}/partners`, label: "Partners" },
    ...(partner ? [{ to: `${prefix}/over/${partner.slug}`, label: ABOUT_LABELS[lang] || "Over ons" }] : []),
  ];

  const langObj = LANGS.find((l) => l.code === lang) || LANGS[0];

  // RECRA Solutions Partner Platform, dark olive nav top bar, matches douchelezer.recrasolutions.com
  const primary = partner?.primary_color || "#39C46F";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(61, 59, 0, 0.96)', borderColor: 'rgba(255,255,255,0.08)' }} data-testid="app-header">
      <div className="container-x flex items-center justify-between py-3.5">
        <Link to={`${prefix}/`} className="flex items-center gap-2.5 group" data-testid="nav-home-link">
          {partner?.logo_url && !logoFailed ? (
            <div
              className="h-10 px-2.5 rounded-md flex items-center"
              style={{
                // Some partner SVGs are designed for dark backgrounds (white logos).
                // Wrap them in a dark chip when we're on a light theme, so they stay legible.
                background: (partner.theme_mode === "light" && partner.logo_on_dark)
                  ? "#0F2A1E"
                  : "transparent",
              }}
            >
              <img
                src={partner.logo_url}
                alt={partner.name}
                className="h-7 max-w-[160px] object-contain"
                data-testid="partner-logo"
                onError={() => setLogoFailed(true)}
              />
            </div>
          ) : (
            <>
              {/* Default brand for the main RECRA Solutions Partner Platform */}
              {partner ? (
                <>
                  <div className="w-10 h-10 rounded-full grid place-items-center text-[#3D3B00] font-bold text-sm shadow-md"
                       style={{ background: '#39C46F' }}>
                    {partner.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-[15px] leading-tight tracking-tight" style={{ color: '#FFFFFF', fontFamily: '"Outfit", sans-serif' }} data-testid="partner-name">
                      {partner.name}
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase font-semibold" style={{ color: '#39C46F' }}>
                      Configurator
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src="/brand/recra-logo-light.png"
                  alt="RECRA Solutions"
                  className="h-8 object-contain"
                  data-testid="partner-name"
                />
              )}
            </>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? "active" : ""}`}
              data-testid={`nav-${n.to.slice(1)}-link`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="btn-ghost hidden sm:inline-flex text-xs" data-testid="lang-switcher-button">
                <Globe className="w-4 h-4" /> {langObj.flag} {lang.toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => {
                  setLang(l.code);
                  // Rewrite URL with new language prefix.
                  // Strip any existing /xx language segment (right after / or /p/:slug)
                  const SUPPORTED = ["nl", "en", "de", "fr", "es", "it", "da"];
                  const parts = location.pathname.split("/").filter(Boolean);
                  let partnerPrefix = "";
                  if (parts[0] === "p" && parts[1]) {
                    partnerPrefix = `/p/${parts[1]}`;
                    parts.splice(0, 2);
                  }
                  if (parts[0] && SUPPORTED.includes(parts[0])) parts.shift();
                  const rest = parts.length ? "/" + parts.join("/") : "";
                  const langSeg = l.code === "nl" ? "" : `/${l.code}`;
                  navigate(`${partnerPrefix}${langSeg}${rest}` || "/");
                }} data-testid={`lang-${l.code}-item`}>
                  <span className="mr-2">{l.flag}</span> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Currency */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="btn-ghost hidden sm:inline-flex text-xs" data-testid="currency-switcher-button">
                <Coins className="w-4 h-4" /> {currency}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {CURRENCIES.map((c) => (
                <DropdownMenuItem key={c.code} onClick={() => setCurrency(c.code)} data-testid={`currency-${c.code}-item`}>
                  {c.symbol} {c.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Cart */}
          <button className="relative btn-ghost" onClick={() => nav("/cart")} data-testid="nav-cart-button">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#D97742] text-white text-[10px] w-5 h-5 grid place-items-center rounded-full font-bold">
                {cartCount}
              </span>
            )}
          </button>

          {/* User */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="btn-ghost flex items-center gap-2" data-testid="user-menu-button">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6FAA8A] to-[#4A8B6F] text-[#0B110F] text-xs grid place-items-center font-semibold">
                    {(user.name || user.email || "U").charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className="w-4 h-4 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 text-xs text-[#A8B3AD]">
                  <div className="font-medium text-[#F4F1EA] truncate">{user.name || user.email}</div>
                  <div className="truncate">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="nav-account-link">
                  <User className="w-4 h-4 mr-2" /> {t.nav.account}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="nav-admin-link">
                    <Settings className="w-4 h-4 mr-2" /> {t.nav.admin}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-sm whitespace-nowrap" data-testid="nav-login-link">{t.nav.login}</Link>
              <Link to="/register" className="btn-primary text-sm whitespace-nowrap hidden sm:inline-flex" data-testid="nav-register-link">
                {t.nav.register}
              </Link>
            </>
          )}

          <button className="btn-ghost lg:hidden" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-button">
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden border-t border-[#243430] bg-[#0B110F]">
          <div className="container-x py-4 flex flex-col gap-2">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} className="py-2 text-[#F4F1EA] font-medium" onClick={() => setMobile(false)}>
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
